import {
    ArticleOutlined,
    AttachMoneyOutlined,
    Business,
    CalendarToday,
    ContentCopy,
    CorporateFareOutlined,
    CreditCard,
    ElectricBoltOutlined,
    Email,
    EscalatorWarning,
    Face4,
    Home,
    Link,
    LocationOn,
    Money,
    Person,
    Phone,
    Portrait,
    PropaneTankOutlined,
    PublishedWithChangesOutlined,
    ReceiptOutlined,
    WaterDropOutlined,
    Work,
} from "@mui/icons-material";
import { Box, Button, ButtonProps, Card, CardContent, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { differenceInYears } from "date-fns";
import { FC } from "react";
import InfoItemWithCopy from "../../../shared/components/InfoItemWithCopy/InfoItemWithCopy";
import { BiometryDetails, IDetalheAnalises } from "../../../shared/services/api/analysis/analysisService";
import { IDetalheUsuario } from "../../../shared/services/api/user/userService";
import { AnalysisActions } from "./AnalysisActions";
import {
    globalFormatCurrencyBRL,
    globalFormatDates,
    globalMaskDocument,
    globalTranslate,
    globalTranslateFinally,
    globalTranslateMaritalStatus,
    globalTranslateMethodPayment,
    globalTranslateProfession,
    globalTranslateSex,
    maskPhone,
    translateBiometryStatus,
} from "../../../shared/utils";
import { AnalysisStatusChip } from "./AnalysisStatusChip";

interface FieldConfig {
    label?: string;
    value: any;
    icon?: JSX.Element;
    image?: string;
    valueFormatter?: (value: string) => string;
    additionalInfo?: string;
    colSpan?: number;
    hidden?: boolean;
    type?: "button" | "box";
    onClick?: () => void;
    buttonProps?: Partial<ButtonProps>;
    boxProps?: {
        backgroundColor?: string;
        textColor?: string;
        padding?: number;
        borderRadius?: number;
        marginTop?: number;
        marginBottom?: number;
    };
}

interface RowConfig {
    fields: FieldConfig[];
}

interface SectionConfig {
    title: string;
    rows: RowConfig[];
}

interface AnalysisCardDataProps {
    row: IDetalheAnalises;
    user?: IDetalheUsuario;
    biometry?: BiometryDetails;
    planList?: any[];
    onclickbuttonResend?: (id: string, email: string) => void;
    onUpdateStatus?: () => void;
	onRevertAnalysis?: () => void;
	showRevertAnalysis?: boolean;
	disableRevertAnalysis?: boolean;
    onResendAnalysis?: () => void;
    showResendAnalysis?: boolean;
    onViewBiometry?: () => void;
    showViewBiometry?: boolean;
    onViewCreditDetails?: () => void;
    showViewCreditDetails?: boolean;

	onGenerateSetupFee?: () => void;
	showGenerateSetupFee?: boolean;

	onConvertToPj?: () => void;
	showConvertToPj?: boolean;
}

export const AnalysisCardData: FC<AnalysisCardDataProps> = ({
    row,
    user,
    biometry,
    planList = [],
    onclickbuttonResend,
    onUpdateStatus,
	onRevertAnalysis,
	showRevertAnalysis,
	disableRevertAnalysis,
    onResendAnalysis,
    showResendAnalysis,
    onViewBiometry,
    showViewBiometry,
    onViewCreditDetails,
    showViewCreditDetails,
	onGenerateSetupFee,
	showGenerateSetupFee,
	onConvertToPj,
	showConvertToPj,
}) => {
    // Logic for Plan List - Use prop if available (even if empty), otherwise parse row.planlist (fallback)
    let finalPlanList = planList !== undefined ? planList : (row.planlist ? (typeof row.planlist === "string" ? JSON.parse(row.planlist) : row.planlist) : []);

    const getPlanStatus = (plan: any) => {
        const status = (plan?.status ?? plan?.plan?.status ?? "").toString().trim().toLowerCase();
        return status;
    };

    // Filter out "Nenhum plano aprovado" if present in description
    if (Array.isArray(finalPlanList)) {
        finalPlanList = finalPlanList.filter((p: any) => p.plan?.description !== "Nenhum plano aprovado" && p.description !== "Nenhum plano aprovado");
        // Keep only active plans (same rule used in approved modals)
        finalPlanList = finalPlanList.filter((p: any) => getPlanStatus(p) === "active");
    }

    const sections: SectionConfig[] = [
        {
            title: "Dados do Locatário",
            rows: [
                {
                    fields: [
                        { label: "Nome", value: row.name, icon: <Person />, colSpan: 4 },
                        {
                            label: "CPF",
                            value: row.document,
                            icon: <Portrait />,
                            valueFormatter: globalMaskDocument,
                            colSpan: 4,
                        },
                        {
                            label: "Nascimento",
                            value: row.birthDate ? row.birthDate?.substring(0, 10) + " 03:00:00" : undefined,
                            icon: <CalendarToday />,
                            additionalInfo: row.birthDate
                                ? !isNaN(differenceInYears(new Date(), new Date(row.birthDate?.substring(0, 10) + " 03:00:00")))
                                    ? `(${differenceInYears(new Date(), new Date(row.birthDate?.substring(0, 10) + " 03:00:00"))} anos)`
                                    : "Não Informado"
                                : "Não Informado",
                            valueFormatter: globalFormatDates,
                            colSpan: 4,
                        },
                        {
                            label: "Nome da Mãe",
                            value: row.motherName,
                            icon: <Face4 />,
                            colSpan: 4,
                        },
                        {
                            label: "Vínculo Empregatício",
                            value: row.employmentRelationShip,
                            icon: <Work />,
                            valueFormatter: globalTranslateProfession,
                            colSpan: 4,
                        },
                        {
                            label: "Estado Civil",
                            value: row.maritalStatus,
                            icon: <EscalatorWarning />,
                            valueFormatter: globalTranslateMaritalStatus,
                            colSpan: 4,
                        },
                        {
                            label: "Sexo",
                            value: (row as any).sex,
                            icon: <Person />,
                            valueFormatter: globalTranslateSex,
                            colSpan: 4,
                        },
                        { label: "Email", value: row.email, icon: <Email />, colSpan: 4 },
                        {
                            label: "Telefone",
                            value: row.phone,
                            icon: <Phone />,
                            valueFormatter: maskPhone,
                            colSpan: 4,
                        },
                    ],
                },
                // Solidarity Tenants Display Logic
                ...(row.solidaryTenants ? (() => {
                    try {
                        const parsedTenants = typeof row.solidaryTenants === "string"
                            ? JSON.parse(row.solidaryTenants)
                            : row.solidaryTenants;

                        if (Array.isArray(parsedTenants) && parsedTenants.length > 0) {
                            return [{
                                fields: [{
                                    value: (
                                        <>
                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, mt: 2, color: "primary.main" }}>
                                                Locatários Solidários
                                            </Typography>
                                            <Box sx={{ width: "100%", mt: 1, backgroundColor: "#f9f9f9", p: 2, borderRadius: 2 }}>
                                                {parsedTenants.map((tenant: any, index: number) => (
                                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < parsedTenants.length - 1 ? "1px dashed #e0e0e0" : "none" }}>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Person sx={{ color: "#757575" }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                                        Nome: <span style={{ fontWeight: 400 }}>{tenant.name || "Não informado"}</span>
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Portrait sx={{ color: "#757575" }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                                        CPF: <span style={{ fontWeight: 400 }}>{tenant.document ? globalMaskDocument(tenant.document) : "Não informado"}</span>
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Email sx={{ color: "#757575" }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                                        Email: <span style={{ fontWeight: 400 }}>{tenant.email || "Não informado"}</span>
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Phone sx={{ color: "#757575" }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                                        Telefone: <span style={{ fontWeight: 400 }}>{tenant.phone ? maskPhone(tenant.phone) : "Não informado"}</span>
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    ),
                                    colSpan: 12,
                                    type: "box" as const,
                                    boxProps: {
                                        padding: 0,
                                        backgroundColor: "transparent",
                                    },
                                }]
                            }];
                        }
                        return [];
                    } catch (e) {
                        console.error("Error parsing solidaryTenants", e);
                        return [];
                    }
                })() : []),
            ],
        },

        {
            title: "Dados da Análise",
            rows: [
                {
                    fields: [
                        {
                            label: "Origem da análise",
                            value: globalTranslate(row?.createdFrom),
                            image: row?.createdFrom === "web_service" ? "/4seg.png" : "/logogar.png",
                            colSpan: 4,
                        },
                        {
                            label: "Criada",
                            value: row.createdAt,
                            icon: <CalendarToday />,
                            valueFormatter: globalFormatDates,
                            colSpan: 4,
                        },
                        {
                            label: "Tipo de Imóvel",
                            value: row.propertyType,
                            icon: <Home />,
                            colSpan: 4,
                        },
                        {
                            label: "Finalidade",
                            value: row.propertyFinally,
                            icon: <Home />,
                            valueFormatter: (value: string) => globalTranslateFinally(value).toString(),
                            colSpan: 4,
                        },
                    ],
                },
                {
                    fields: [
                        {
                            label: "Tombamento",
                            value: row.isTipping === "1" ? "Sim" : "Não",
                            icon: <PublishedWithChangesOutlined />,
                            colSpan: 4,
                        },
                        {
                            label: "Vigência",
                            value: `${row.rentMonthsCount} meses`,
                            icon: <CalendarToday />,
                            colSpan: 4,
                        },

                    ],
                },
                {
                    fields: [
                        {
                            label: `Link de Biometria${(biometry?.updatedAt || (biometry as any)?.updated_at || biometry?.createdAt || (biometry as any)?.created_at) ? ` (atualizado em ${globalFormatDates(biometry?.updatedAt || (biometry as any)?.updated_at || biometry?.createdAt || (biometry as any)?.created_at)})` : ""}`,
                            value: (biometry?.urlShort || (biometry as any)?.url_short) ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary" component="div" sx={{ wordBreak: "break-all" }}>
                                        <a href={biometry?.urlShort || (biometry as any)?.url_short} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                            {biometry?.urlShort || (biometry as any)?.url_short}
                                        </a>
                                    </Typography>
                                    <Tooltip title="Copiar link">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                navigator.clipboard.writeText(biometry?.urlShort || (biometry as any)?.url_short || "");
                                            }}
                                        >
                                            <ContentCopy fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : "Não enviado",
                            icon: <Link />,
                            colSpan: 12,
                        },
                    ],
                },
                {
                    fields: [
                        {
                            label: "Endereço",
                            value: `${row.addressStreet}, ${row.addressNumber} - ${row.addressComplement} - ${row.addressDistrict} - ${row.addressCity} - ${row.addressState} - ${row.addressZipcode}`,
                            icon: <LocationOn />,
                            colSpan: 12,
                        },
                    ],
                },
                {
                    fields: [
                        {
                            label: "Parceiro",
                            value: row.estate?.name ?? row.client?.name ?? "Não informado",
                            icon: <Business />,
                            colSpan: 12,
                        },
                    ],
                },
                // Plan Display Logic
                ...(finalPlanList.length > 0 &&
                    row.status !== "canceled" && row.status !== "Cancelada" &&
                    (row.status === "issued" || row.status === "awaiting_signature" || row.status === "awaiting_biometria")
                    ? [
                        {
                            fields: [
                                {
                                    value: (
                                        <>
                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                                                Planos Aprovados
                                            </Typography>
                                            <Box sx={{ width: "100%", mt: 1, backgroundColor: "#f9f9f9", p: 2, borderRadius: 2 }}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #e0e0e0", mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                        Plano
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 6 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                            Taxa
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                            Valor mensal
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {finalPlanList.map((plan: any, index: number) => (
                                                    <Box key={index} sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px dashed #e0e0e0" }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#4c4c4d" }}>
                                                            {plan.plan?.description || "Plano"}
                                                        </Typography>
                                                        <Box sx={{ display: "flex", gap: 6 }}>
                                                            <Typography variant="body2" sx={{ color: "#757575" }}>
                                                                {row.propertyFinally === "residential" ? `${plan.residentialFee}%` : `${plan.commercialFee}%`}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c4c4d" }}>
                                                                {row.propertyFinally === "residential"
                                                                    ? globalFormatCurrencyBRL(
                                                                        plan.planId.includes("__charges")
                                                                            ? (
                                                                                (parseFloat(row.rentAmount) +
                                                                                    parseFloat(row.condominiumAmount) +
                                                                                    parseFloat(row.iptuAmount)) *
                                                                                (parseFloat(plan.residentialFee) / 100)
                                                                            ).toFixed(2)
                                                                            : (
                                                                                parseFloat(row.rentAmount) *
                                                                                (parseFloat(plan.residentialFee) / 100)
                                                                            ).toFixed(2)
                                                                    )
                                                                    : globalFormatCurrencyBRL(
                                                                        plan.planId.includes("__charges")
                                                                            ? (
                                                                                (parseFloat(row.rentAmount) +
                                                                                    parseFloat(row.condominiumAmount) +
                                                                                    parseFloat(row.iptuAmount)) *
                                                                                (parseFloat(plan.commercialFee) / 100)
                                                                            ).toFixed(2)
                                                                            : (
                                                                                parseFloat(row.rentAmount) *
                                                                                (parseFloat(plan.commercialFee) / 100)
                                                                            ).toFixed(2)
                                                                    )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    ),
                                    colSpan: 12,
                                    type: "box" as const,
                                    boxProps: {
                                        padding: 0,
                                        backgroundColor: "transparent",
                                    },
                                },
                            ],
                        },
                    ]
                    : []),
            ],
        },

        {
            title: "Valores",
            rows: [
                {
                    fields: [
                        {
                            label: "Valor do aluguel",
                            value: row.rentAmount,
                            icon: <AttachMoneyOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "LMI",
                            value: row.lmi,
                            icon: <PublishedWithChangesOutlined />,
                            colSpan: 4,
                        },
                        {
                            label: "Valor do Condomínio ",
                            value: row.condominiumAmount,
                            icon: <CorporateFareOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "Valor do IPTU",
                            value: row.iptuAmount,
                            icon: <ReceiptOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "Valor da Energia",
                            value: row.electricEnergyAmount,
                            icon: <ElectricBoltOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "Valor da Água",
                            value: row.waterAmount,
                            icon: <WaterDropOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "Valor do Gás",
                            value: row.gasAmount,
                            icon: <PropaneTankOutlined />,
                            valueFormatter: (value: string) => globalFormatCurrencyBRL(value).toString(),
                            colSpan: 4,
                        },
                        {
                            label: "Taxa de Setup",
                            value: row.transaction?.rawAmount ? globalFormatCurrencyBRL(row.transaction.rawAmount) : "Não cobrada",
                            icon: <AttachMoneyOutlined />,
                            colSpan: 4,
                        },
                        {
                            label: "Reenviar Link Setup",
                            value: "Reenviar",
                            type: "button",
                            hidden: row.transaction?.status !== "pending",
                            onClick: () => onclickbuttonResend && onclickbuttonResend(row.installmentSetup?.id as string, row.email),
                            colSpan: 4,
                        },
                    ],
                },
            ],
        },
        {
            title: "Dados do Locador",
            rows: [
                {
                    fields: [
                        {
                            label: "Nome",
                            value: row.lessorName,
                            icon: <Person />,
                            colSpan: 4,
                        },
                        {
                            label: "Tipo do Locador",
                            value: row.lessorTypePerson,
                            icon: <Person />,
                            colSpan: 4,
                        },
                        {
                            label: "CPF",
                            value: row.lessorCpf,
                            icon: <Portrait />,
                            valueFormatter: globalMaskDocument,
                            colSpan: 4,
                        },
                        {
                            label: "Nascimento",
                            value: row.lessorBirthDate ? row.lessorBirthDate?.substring(0, 10) + " 03:00:00" : undefined,
                            icon: <CalendarToday />,
                            additionalInfo: row.lessorBirthDate
                                ? !isNaN(differenceInYears(new Date(), new Date(row.lessorBirthDate?.substring(0, 10) + " 03:00:00")))
                                    ? `(${differenceInYears(new Date(), new Date(row.lessorBirthDate?.substring(0, 10) + " 03:00:00"))} anos)`
                                    : "Não Informado"
                                : "Não Informado",
                            valueFormatter: globalFormatDates,
                            colSpan: 4,
                        },
                        {
                            label: "Telefone",
                            value: row.lessorPhone,
                            icon: <Phone />,
                            valueFormatter: maskPhone,
                            colSpan: 4,
                        },
                        {
                            label: "Email",
                            value: row.lessorEmail,
                            icon: <Email />,
                            colSpan: 4,
                        },
                    ],
                },
            ],
        },
        ...((row.status === "canceled" || row.status === "expired")
            ? [
                {
                    title: row.status === "expired" ? "Expiração" : "Cancelamento",
                    rows: [
                        {
                            fields: [
                                {
                                    label: "Motivo",
                                    value: row.canceledType,
                                    icon: <ArticleOutlined />,
                                    colSpan: 12,
                                },
                                {
                                    label: "Descrição",
                                    value: row.canceledReason,
                                    icon: <ArticleOutlined />,
                                    colSpan: 12,
                                },
                            ],
                        },
                    ],
                },
            ]
            : []),
        {
            title: "Dados do usuário",
            rows: [
                {
                    fields: [
                        {
                            label: "Nome",
                            value: user?.name,
                            icon: <Person />,
                            colSpan: 4,
                        },
                        {
                            label: "Email",
                            value: user?.email,
                            icon: <Email />,
                            colSpan: 4,
                        },
                        {
                            label: "Última atualização",
                            value: (user as any)?.updatedAt,
                            icon: <CalendarToday />,
                            valueFormatter: globalFormatDates,
                            colSpan: 4,
                        },
                    ],
                },
            ],
        },
    ];

    return (
        <Card sx={{ margin: "auto", px: 4, borderRadius: 4, mt: 0.5 }}>
            <CardContent>
                {
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        <AnalysisStatusChip status={row.status} />
                        <AnalysisActions
                            onUpdateStatus={onUpdateStatus}
							onRevertAnalysis={onRevertAnalysis}
							showRevertAnalysis={showRevertAnalysis}
							disableRevertAnalysis={disableRevertAnalysis}
                            onResendAnalysis={onResendAnalysis}
                            showResendAnalysis={showResendAnalysis}
                            onViewBiometry={onViewBiometry}
                            showViewBiometry={showViewBiometry}
                            onViewCreditDetails={onViewCreditDetails}
                            showViewCreditDetails={showViewCreditDetails}
                            onGenerateSetupFee={onGenerateSetupFee}
                            showGenerateSetupFee={showGenerateSetupFee}
                            onConvertToPj={onConvertToPj}
                            showConvertToPj={showConvertToPj}
                        />
                    </Box>
                }

                {row.status === "pre_approved" && (
                    <Box
                        sx={{
                            width: "100%",
                            border: "1px solid #eac1c1",
                            backgroundColor: "#fdeded",
                            padding: 2,
                            borderRadius: 1,
                            mb: 2,
                            mt: 2,
                        }}
                    >
                        <Box display={"flex"} gap={1} alignItems={"center"}>
                            <Typography variant="body1" sx={{ color: "#d54949" }}>
                                Analise Pré-Aprovada com valor de até
                            </Typography>
                            <Typography variant="body1" sx={{ color: "#d54949", fontWeight: "600", fontSize: "18px" }}>
                                {parseFloat(row.creditLimit || "0").toLocaleString("pt-br", {
                                    style: "currency",
                                    currency: "BRL",
                                })}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: "#d54949", fontWeight: "bold", mt: 1 }}>
                            Próximos passos:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#d54949" }}>
                            Você deverá ajustar o valor do aluguel negociando junto ao proprietário ou buscar outro imóvel que possua o valor do aluguel
                            compatível com este valor pré-aprovado.
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#d54949" }}>
                            Será necessário clicar em REANALISE para ajustar o valor do aluguel (conforme o limite aprovado) e, caso tenha buscado outro imóvel,
                            alterar os DADOS DO IMÓVEL. (Não é necessário realizar uma nova análise)
                        </Typography>
                    </Box>
                )}

                {sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} style={{ marginBottom: "16px" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#4c4c4d",
                                fontWeight: 500,
                                marginBottom: "12px",
                            }}
                        >
                            {section.title}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {section.rows.map((row, rowIndex) =>
                                row.fields
                                    .filter((field) => !field.hidden)
                                    .map((field, fieldIndex) => (
                                        <Grid item xs={field.colSpan || 12} sm={field.colSpan || 6} key={`${rowIndex}-${fieldIndex}`}>
                                            {field.type === "button" ? (
                                                <Button startIcon={field.icon} fullWidth onClick={field.onClick} {...field.buttonProps}>
                                                    {field.label}
                                                </Button>
                                            ) : field.type === "box" ? (
                                                <Box
                                                    sx={{
                                                        width: "98%",
                                                        backgroundColor: field.boxProps?.backgroundColor || "rgba(255, 0, 0, 0.05)",
                                                        padding: field.boxProps?.padding ?? 2,
                                                        borderRadius: field.boxProps?.borderRadius ?? 1,
                                                        marginTop: field.boxProps?.marginTop ?? 1,
                                                        marginBottom: field.boxProps?.marginBottom ?? 2,
                                                    }}
                                                >
                                                    <Typography variant="body1" color={field.boxProps?.textColor || "red"} sx={{ width: "100%" }}>
                                                        {field.value}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <InfoItemWithCopy
                                                    label={field.label || ""}
                                                    value={field.valueFormatter ? field.valueFormatter(field.value || "") : field.value || ""}
                                                    icon={field.icon}
                                                    image={field.image}
                                                    additionalInfo={field.additionalInfo}
                                                />
                                            )}
                                        </Grid>
                                    ))
                            )}
                        </Grid>
                        {sectionIndex < sections.length - 1 && <Divider sx={{ my: 3 }} />}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};
